/**
 * Flow Optimizer — queue-readiness mutation, both directions. A WAITING bank
 * element becomes In Queue when marked ready (`ready: true`, the default —
 * the backend also stamps `queuedAt`); a QUEUED element moves back to Waiting
 * with `ready: false` (off the promotion line until moved to the queue
 * again). PATCH on the same provider-agnostic bank routes as useQueuePriority,
 * then refresh the queue.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { FlowLayer } from '@trafficpulse/shared';

import api from '@/services/api';
import type { QueueProvider } from '@/features/trackers/flow-optimizer/queue-provider';

import { queueKeys } from './useFlowOptimizerQueue';
import { BANK_PATH } from './useQueuePriority';

export interface MarkReadyArgs {
  layer: FlowLayer;
  bankId: string;
  /** false = move a queued element back to WAITING. Default true. */
  ready?: boolean;
}

/** Move an element WAITING → IN QUEUE (default) or IN QUEUE → WAITING. */
export function useMarkReady(
  campaignId: string,
  provider: QueueProvider = 'voluum',
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ layer, bankId, ready }: MarkReadyArgs) =>
      api.patch(`/voluum/${BANK_PATH[layer]}/${bankId}`, {
        queueReady: ready ?? true,
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: queueKeys.queue(provider, campaignId) }),
  });
}
